import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {number} */
  #announceFrame = 0;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Resolves the section id that morphSection should re-render.
   *
   * Falls back to the closest ancestor that carries [data-section-id]
   * (such as <cart-items-component>) when the component itself does not
   * have the attribute. This makes the element work both in the cart
   * drawer — where the enclosing snippet renders with `section_id:
   * section.id` — and on the cart page, where the `_cart-summary` block
   * is rendered via `content_for` and cannot receive kwargs.
   *
   * @returns {string | null}
   */
  #resolveSectionId() {
    if (typeof this.dataset.sectionId === 'string' && this.dataset.sectionId !== '') {
      return this.dataset.sectionId;
    }

    const ancestor = this.closest('[data-section-id]');
    if (ancestor instanceof HTMLElement && typeof ancestor.dataset.sectionId === 'string' && ancestor.dataset.sectionId !== '') {
      return ancestor.dataset.sectionId;
    }

    return null;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement)) return;

    const sectionId = this.#resolveSectionId();
    if (sectionId === null) return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      this.#announce('');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(sectionId, newHtml);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) console.error(error);
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    // The previous guard required `event.target instanceof HTMLElement`,
    // which rejected clicks on the inner X icon — SVG elements extend
    // SVGElement, not HTMLElement, so clicks on the visual icon silently
    // no-op'd while only clicks on the button's padding worked. Widening
    // the guard to `Element` accepts both HTML buttons and the SVG paths
    // that sit inside them. 2026-05-12 cart-bug follow-up to Batch 1.
    // Handle KeyboardEvent and MouseEvent independently: the previous OR
    // chain evaluated `!(event instanceof MouseEvent)` as true for every
    // KeyboardEvent, so keyboard Enter could never remove a discount
    // (WCAG 2.1.1, NH-A11Y-023).
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter') return;
    } else if (!(event instanceof MouseEvent)) {
      return;
    }
    if (!(event.target instanceof Element)) {
      return;
    }

    const sectionId = this.#resolveSectionId();
    if (sectionId === null) return;

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(sectionId, data.sections[sectionId]);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) console.error(error);
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
    this.#announce(target.textContent?.trim() ?? '');
  }

  /**
   * Returns the live region used for discount messages. The component's own
   * visually hidden region is preferred: in the cart drawer the page-level
   * announcer sits outside the modal dialog, where it is inert and silent.
   * @returns {HTMLElement | null}
   */
  #announcer() {
    const local = this.querySelector('[data-cart-discount-announcer]');
    if (local instanceof HTMLElement) return local;
    const global = document.getElementById('sr-announcer');
    return global instanceof HTMLElement ? global : null;
  }

  /**
   * Writes a message into the always-rendered live region. The region is
   * emptied first and filled on the next frame, so a repeated message is
   * still announced.
   * @param {string} message
   */
  #announce(message) {
    const region = this.#announcer();
    if (!region) return;
    if (this.#announceFrame) cancelAnimationFrame(this.#announceFrame);
    this.#announceFrame = 0;
    region.textContent = '';
    if (!message) return;
    this.#announceFrame = requestAnimationFrame(() => {
      this.#announceFrame = 0;
      region.textContent = message;
    });
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
